/**
 * Permission Constants
 * Centralized definition of all permissions in the application
 * Each permission maps to an array of roles that have that permission
 */

import { ROLES, ROLE_GROUPS } from "./roles";

export const PERMISSIONS = {
  // Dashboard
  VIEW_DASHBOARD: [ROLES.ADMIN, ROLES.COMPANY_ADMIN, ROLES.KEY_ACCOUNT],

  // Projects
  VIEW_PROJECTS: [ROLES.PROJECT_ADMIN, ROLES.COMPANY_ADMIN],
  CREATE_PROJECT: [ROLES.PROJECT_ADMIN],
  EDIT_PROJECT: [ROLES.PROJECT_ADMIN],
  DELETE_PROJECT: [ROLES.PROJECT_ADMIN],
  VIEW_PROJECT_OVERVIEW: [ROLES.PROJECT_ADMIN],
  UPLOAD_TARGET_GROUPS: [ROLES.PROJECT_ADMIN],
  EDIT_EMAIL: [ROLES.PROJECT_ADMIN],

  // Assessments
  VIEW_ASSESSMENTS: [ROLES.PROJECT_ADMIN],
  CREATE_ASSESSMENT: [ROLES.PROJECT_ADMIN],
  EDIT_ASSESSMENT: [ROLES.PROJECT_ADMIN],
  COPY_ASSESSMENTS: [ROLES.PROJECT_ADMIN],
  VIEW_FEEDBACK_PROVIDERS: [ROLES.PROJECT_ADMIN],
  INVITE_FEEDBACK_PROVIDERS: [ROLES.PROJECT_ADMIN],

  // Users - Key Users
  VIEW_KEY_USERS: [ROLES.ADMIN, ROLES.COMPANY_ADMIN],
  CREATE_KEY_USER: [ROLES.COMPANY_ADMIN],
  EDIT_KEY_USER: [ROLES.COMPANY_ADMIN],

  // Users - Participants
  VIEW_PARTICIPANTS: [ROLES.PROJECT_ADMIN],
  EDIT_PARTICIPANT: [ROLES.PROJECT_ADMIN],
  VIEW_PARTICIPANT_INFO: [ROLES.PROJECT_ADMIN],

  // Companies
  VIEW_COMPANIES: [ROLES.ADMIN, ROLES.COMPANY_ADMIN],
  CREATE_COMPANY: [ROLES.ADMIN],
  EDIT_COMPANY: [ROLES.ADMIN],

  // Reports
  VIEW_REPORTS: [ROLES.ADMIN, ROLES.PROJECT_ADMIN],

  // Downloads
  VIEW_DOWNLOADS: [ROLES.PROJECT_ADMIN],

  // MindCloud: sopstveni prostor ("My MindCloud") imaju admin role; standard
  // user vidi samo "Shared with me" (ulaz sa dashboarda, ne iz menija).
  VIEW_MINDCLOUD: [
    ROLES.ADMIN,
    ROLES.COMPANY_ADMIN,
    ROLES.PROJECT_ADMIN,
    ROLES.KEY_ACCOUNT,
    ROLES.QUESTIONNAIRE_CREATOR,
    ROLES.TRANSLATION_MANAGER,
  ],

  // Questionnaires
  VIEW_QUESTIONNAIRES: [ROLES.QUESTIONNAIRE_CREATOR],
  CREATE_QUESTIONNAIRE: [ROLES.QUESTIONNAIRE_CREATOR],
  EDIT_QUESTIONNAIRE: [ROLES.QUESTIONNAIRE_CREATOR],
  TRANSLATE_QUESTIONNAIRE: [ROLES.QUESTIONNAIRE_CREATOR],
  PREVIEW_QUESTIONNAIRE: [ROLES.QUESTIONNAIRE_CREATOR, ROLES.PROJECT_ADMIN],

  // Profiles
  VIEW_PROFILES: [ROLES.QUESTIONNAIRE_CREATOR],
  EDIT_PROFILE: [ROLES.QUESTIONNAIRE_CREATOR],

  // Admin - Questionnaire Company Management
  MANAGE_QUESTIONNAIRE_COMPANIES: [ROLES.ADMIN],

  // Translation Platform
  VIEW_TRANSLATION_PLATFORM: [ROLES.ADMIN, ROLES.TRANSLATION_MANAGER, ROLES.TRANSLATOR],
  VIEW_TRANSLATION_OVERVIEW: [ROLES.ADMIN, ROLES.TRANSLATION_MANAGER, ROLES.TRANSLATOR],
  MANAGE_TRANSLATION_GROUPS: [ROLES.ADMIN, ROLES.TRANSLATION_MANAGER],
  MANAGE_TRANSLATIONS: [ROLES.ADMIN, ROLES.TRANSLATION_MANAGER, ROLES.TRANSLATOR],
  MANAGE_TRANSLATION_DELEGATIONS: [ROLES.ADMIN],

  // Admin - Audit & Error Logs
  VIEW_AUDIT_LOGS: [ROLES.ADMIN],

  // Profile (own profile) - all authenticated users
  VIEW_OWN_PROFILE: ROLE_GROUPS.ALL_ROLES,

  // Home/Tasks - all authenticated users can view
  VIEW_HOME: ROLE_GROUPS.ALL_ROLES,
  VIEW_TASKS: ROLE_GROUPS.ALL_ROLES,

  // Survey/Assessment participation - all authenticated users can participate
  TAKE_SURVEY: ROLE_GROUPS.ALL_ROLES,
  VIEW_TARGET_GROUP: ROLE_GROUPS.ALL_ROLES,
  AGREE_TO_TERMS: ROLE_GROUPS.ALL_ROLES,
  WRITE_REPORT: ROLE_GROUPS.ALL_ROLES,

  // Excel upload
  UPLOAD_EXCEL: [ROLES.PROJECT_ADMIN],

  // Project Features
  MANAGE_PROJECT_FEATURES: [ROLES.PROJECT_ADMIN],
  CONFIGURE_TARGET_GROUP_SETTINGS: [ROLES.PROJECT_ADMIN],
  CONFIGURE_SURVEY_SETTINGS: [ROLES.PROJECT_ADMIN],
  CONFIGURE_SUCCESS_FACTORS: [ROLES.PROJECT_ADMIN],
  CONFIGURE_EVALUATION_LEVEL: [ROLES.PROJECT_ADMIN],
  CONFIGURE_PREVIOUS_ANSWERS: [ROLES.PROJECT_ADMIN],
  CONFIGURE_CLASSIFICATION_LEVELS: [ROLES.PROJECT_ADMIN],
  CONFIGURE_LIMITATION_OF_SCORES: [ROLES.PROJECT_ADMIN],
  CONFIGURE_SELF_REGISTRATION: [ROLES.PROJECT_ADMIN],
  CONFIGURE_COMPETENCY_MODEL_IN_QUESTIONNAIRE: [ROLES.PROJECT_ADMIN],
};

/**
 * Helper function to get all roles that have a specific permission
 */
export const getRolesForPermission = (permission) => {
  return PERMISSIONS[permission] || [];
};

/**
 * Helper function to check if a permission exists
 */
export const permissionExists = (permission) => {
  return Object.prototype.hasOwnProperty.call(PERMISSIONS, permission);
};
